import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactElement } from 'react';
import type { ClavePermiso } from '@copathy/shared';
import { useMe, usePuede } from './lib/auth';
import { Login } from './paginas/Login';
import { Registro } from './paginas/Registro';
import { VerificarCorreo } from './paginas/VerificarCorreo';
import { Inicio } from './paginas/Inicio';
import { Clientes } from './paginas/Clientes';
import { ClienteDetalle } from './paginas/ClienteDetalle';
import { Usuarios } from './paginas/Usuarios';
import { UsuarioPerfil } from './paginas/UsuarioPerfil';
import { Cuestionarios } from './paginas/Cuestionarios';
import { Aplicaciones } from './paginas/Aplicaciones';
import { Actividades } from './paginas/Actividades';
import { CuestionarioArmar } from './paginas/CuestionarioArmar';
import { Configuracion } from './paginas/Configuracion';
import { ActivarCuenta } from './paginas/ActivarCuenta';
import { EnConstruccion } from './paginas/EnConstruccion';
import { Shell } from './componentes/Shell';
import { Marca } from './componentes/Marca';

/**
 * Secciones declaradas en el menu que aun no existen.
 *
 * Cada una lleva el permiso que le corresponde: aunque la pantalla no este
 * hecha, quien no deba verla tampoco debe poder llegar a la direccion.
 */
const PENDIENTES: {
  ruta: string;
  titulo: string;
  detalle: string;
  permiso?: ClavePermiso;
}[] = [
  {
    ruta: '/programas',
    titulo: 'Programas y competencias',
    detalle:
      'Las cuatro areas de Copathy y las competencias que mide cada una. De aqui cuelga cada pregunta.',
    permiso: 'empresas.ver',
  },
  {
    ruta: '/resultados',
    titulo: 'Resultados',
    detalle: 'Resultados por competencia y comparativa antes/despues, de todas las empresas.',
    permiso: 'resultados.ver_todas_empresas',
  },
  {
    ruta: '/auditoria',
    titulo: 'Auditoria de accesos',
    detalle: 'Quien de Copathy vio datos de que empresa, y cuando.',
    permiso: 'auditoria.ver',
  },
  {
    ruta: '/mis-resultados',
    titulo: 'Resultados de mis programas',
    detalle:
      'Resultados agregados de tu empresa. Nunca respuestas individuales: eso es confidencial.',
    permiso: 'resultados.ver_propia_empresa',
  },
  {
    ruta: '/participacion',
    titulo: 'Participacion',
    detalle: 'Quien completo cada actividad. Participacion, no contenido.',
    permiso: 'participacion.ver',
  },
  {
    ruta: '/historial',
    titulo: 'Mi historial',
    detalle: 'Lo que has respondido y cuando. Solo tu lo ves.',
    permiso: 'historial.ver_propio',
  },
  { ruta: '/perfil', titulo: 'Mi perfil', detalle: 'Tus datos y el cambio de contrasena.' },
];

function Cargando() {
  return (
    <div className="flex min-h-full items-center justify-center">
      <div className="text-center">
        <Marca tamano="md" />
        <div
          role="status"
          aria-label="Cargando"
          className="mx-auto mt-6 h-5 w-5 animate-spin rounded-full border-2 border-marino-200 border-t-naranja-500"
        />
      </div>
    </div>
  );
}

/** Guardia de ruta. Solo oculta: el servidor decide de verdad. */
function Privada({ children, permiso }: { children: ReactElement; permiso?: ClavePermiso }) {
  const { usuario, cargando } = useMe();
  const puede = usePuede(permiso ?? 'historial.ver_propio');

  if (cargando) return <Cargando />;
  if (!usuario) return <Navigate to="/entrar" replace />;
  // Sin el permiso, se manda al inicio en vez de mostrar un «no puedes»: llegó
  // a una dirección que para su rol no existe.
  if (permiso && !puede) return <Navigate to="/" replace />;
  return children;
}

function SoloInvitados({ children }: { children: ReactElement }) {
  const { usuario, cargando } = useMe();
  if (cargando) return <Cargando />;
  // Quien ya entró no vuelve a ver el login: lo confunde y ya tiene sesión.
  if (usuario) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/entrar"
        element={
          <SoloInvitados>
            <Login />
          </SoloInvitados>
        }
      />
      <Route
        path="/registro"
        element={
          <SoloInvitados>
            <Registro />
          </SoloInvitados>
        }
      />
      {/*
        Verificar el correo NO es una ruta de invitados: alguien puede abrir el
        enlace desde un dispositivo donde ya tiene sesión de otra cuenta, y
        mandarlo al inicio dejaría la verificación sin hacer.
      */}
      <Route path="/verificar" element={<VerificarCorreo />} />
      {/*
        Tampoco es ruta de invitados: alguien puede abrir su invitación desde un
        dispositivo donde ya hay sesión de otra cuenta, y mandarlo al inicio
        dejaría la cuenta sin activar y el enlace sin usar.
      */}
      <Route path="/activar" element={<ActivarCuenta />} />

      {/*
        Todo lo de adentro cuelga del Shell: el menu y el encabezado se montan
        UNA vez y no se vuelven a dibujar al navegar. Con el shell dentro de
        cada pagina, cambiar de seccion reiniciaba el estado del menu -- el
        plegado, el scroll de la lista -- en cada clic.
      */}
      <Route
        element={
          <Privada>
            <Shell />
          </Privada>
        }
      >
        <Route path="/" element={<Inicio />} />
        <Route
          path="/clientes"
          element={
            <Privada permiso="empresas.ver">
              <Clientes />
            </Privada>
          }
        />
        <Route
          path="/usuarios"
          element={
            <Privada permiso="usuarios.administrar">
              <Usuarios />
            </Privada>
          }
        />
        <Route
          path="/usuarios/:id"
          element={
            <Privada permiso="usuarios.administrar">
              <UsuarioPerfil />
            </Privada>
          }
        />
        <Route
          path="/clientes/:id"
          element={
            <Privada permiso="empresas.ver">
              <ClienteDetalle />
            </Privada>
          }
        />
        {/*
          El listado pide `cuestionarios.ver` y el constructor también: quien solo
          puede ver entra a la misma pantalla, y ahí adentro `usePuede` esconde lo
          que edita. Pedir `armar` para entrar dejaría al consultor sin poder
          consultar el instrumento que va a aplicar.
        */}
        <Route
          path="/aplicaciones"
          element={
            <Privada permiso="aplicaciones.ver">
              <Aplicaciones />
            </Privada>
          }
        />
        <Route
          path="/actividades"
          element={
            <Privada permiso="actividades.responder">
              <Actividades />
            </Privada>
          }
        />
        <Route
          path="/cuestionarios"
          element={
            <Privada permiso="cuestionarios.ver">
              <Cuestionarios />
            </Privada>
          }
        />
        <Route
          path="/cuestionarios/:id"
          element={
            <Privada permiso="cuestionarios.ver">
              <CuestionarioArmar />
            </Privada>
          }
        />
        {/*
          La dirección vieja sigue viva y redirige. Alguien puede tenerla guardada
          en un marcador, y un enlace que lleva al inicio sin decir nada se lee
          como una aplicación rota (D-035).
        */}
        <Route path="/empresas" element={<Navigate to="/clientes" replace />} />
        {/*
          Las solicitudes son ahora una pestaña dentro de Usuarios: una solicitud
          es una cuenta que todavía no existe, y tenerlas en dos sitios obligaba a
          recordar dónde mirar. La dirección vieja redirige — alguien puede
          tenerla en un marcador.
        */}
        <Route path="/solicitudes" element={<Navigate to="/usuarios" replace />} />
        {/*
          «Trabajadores» era la misma lista que «Usuarios de clientes»: un
          trabajador ES un usuario con ese rol. Tres sitios para responder «quién
          entra a la plataforma» obligaba a recordar en cuál mirar (D-045).
        */}
        <Route path="/trabajadores" element={<Navigate to="/usuarios" replace />} />

        {/*
          Las secciones que el menu ya declara y que todavia no estan
          construidas. Sin estas rutas, el comodin de mas abajo las mandaba al
          inicio SIN DECIR NADA -- y un clic que no lleva a ninguna parte se lee
          como una aplicacion rota, no como una seccion pendiente.
        */}
        <Route path="/configuracion/*" element={<Configuracion />} />

        {PENDIENTES.map(({ ruta, titulo, detalle, permiso }) => (
          <Route
            key={ruta}
            path={ruta}
            element={
              <Privada permiso={permiso}>
                <EnConstruccion titulo={titulo} detalle={detalle} />
              </Privada>
            }
          />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
